import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Container,
  Typography,
  Link as MuiLink,
  Tooltip,
  IconButton,
  Divider,
  Button
} from '@mui/material'
import { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useMediaQuery } from 'react-responsive'
import { useParams } from 'react-router-dom'
import { adminService } from 'shared/api'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import EditIcon from '@mui/icons-material/Edit'
import JournalIcon from '@mui/icons-material/EventNote'
import { GiftCardPaymentStatus, GiftCardStatus } from 'shared/models'
import { giftCardStatusBackground, giftCardStatusColor, giftCardStatusText, paymentStatusText } from './status-utils'
import { GiftStaffCommentDialog } from './gift-card-staff-comment-dialog'
import { GiftCardPaymentStatusDialog } from './gift-card-payment-status-dialog'
import { GiftCardStatusDialog } from './gift-card-status-dialog'
import { GiftCardPaymentLinkDialog } from './gift-card-payment-link-dialog'
import { GiftCardJournalDialog } from './gift-card-journal'
import { GiftCardReedemDialog } from './gift-card-reedem-dialog'

function GiftCardView() {
  const isMobile = useMediaQuery({ query: '(max-width: 640px)' })

  const { giftCardId } = useParams<{ giftCardId: string }>()

  const [isDeleting, setIsDeleting] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [editStatusDialogOpen, setEditStatusDialogOpen] = useState(false)
  const [editPaymentStatusDialogOpen, setEditPaymentStatusDialogOpen] = useState(false)
  const [editStaffCommentDialogOpen, setEditStaffCommentDialogOpen] = useState(false)
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false)
  const [journalIsOpened, setJournalIsOpened] = useState(false)
  const [reedemDialogOpen, setReedemDialogOpen] = useState(false)

  const client = useQueryClient()

  const { data, isLoading, isFetching, refetch } = useQuery(['gift-card', giftCardId], ({ signal }) =>
    adminService.giftCard.getGiftCard(giftCardId!, { signal })
  )

  function cancelDelete() {
    setIsDeleting(false)
  }

  function handleDelete() {
    setIsDeleting(true)
  }

  async function onDelete() {
    setIsDeleting(false)
    setProcessing(true)

    try {
      await adminService.giftCard.cancelGiftCard(Number(giftCardId))
      client.invalidateQueries(['gift-card', giftCardId])
    } finally {
      setProcessing(false)
    }
  }

  function handleJournalOpen() {
    setJournalIsOpened(true)
  }

  function closeJournalDialog() {
    setJournalIsOpened(false)
  }

  function handleStatusChange() {
    setEditStatusDialogOpen(true)
  }

  function closeEditStatusDialog() {
    setEditStatusDialogOpen(false)
    refetch()
  }

  function handlePaymentStatusChange() {
    setEditPaymentStatusDialogOpen(true)
  }

  function closeEditPaymentStatusDialog() {
    setEditPaymentStatusDialogOpen(false)
    refetch()
  }

  function handleStaffCommentChange() {
    setEditStaffCommentDialogOpen(true)
  }

  function closeEditStaffCommentDialog() {
    setEditStaffCommentDialogOpen(false)
    refetch()
  }

  function handlePaymentLinkClick() {
    setPaymentLinkDialogOpen(true)
  }

  function closePaymentLinkDialog() {
    setPaymentLinkDialogOpen(false)
    refetch()
  }

  function handleRedeem() {
    setReedemDialogOpen(true)
  }

  function closeReedemDialog() {
    setReedemDialogOpen(false)
    refetch()
  }

  return (
    <Container maxWidth="lg" style={{ padding: isMobile ? 0 : undefined }}>
      {!!isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && !!data?.data && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.5rem',
              justifyContent: 'space-between',
              mb: 3,
              minHeight: 40
            }}
          >
            <Typography variant="h6">{`Сертификат ${data.data.code} от ${Intl.DateTimeFormat('ru', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            }).format(data.data.dateTime)}`}</Typography>
          </Box>
          <Card elevation={1}>
            <Box>
              <div style={{ display: 'flex', gap: '0 1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ padding: '1rem', display: 'flex', gap: '1rem', alignItems: 'center', flex: 1 }}>
                  {!!data.data && (
                    <>
                      <div style={{ padding: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <PersonIcon />
                      </div>
                      <div>
                        <Typography variant="body1" fontWeight={500}>
                          {data.data.name}
                        </Typography>
                        <div
                          style={{
                            display: 'flex',
                            gap: '0.25rem 1rem',
                            alignItems: 'center',
                            marginTop: '2px',
                            flexWrap: 'wrap'
                          }}
                        >
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <PhoneIcon fontSize="small" style={{ opacity: 0.72, width: '16px', height: '16px' }} />
                            <MuiLink style={{ fontSize: '13px' }} href={`tel:${data.data.phoneNumber}`}>
                              {data.data.phoneNumber}
                            </MuiLink>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <EmailIcon fontSize="small" style={{ opacity: 0.72, width: '16px', height: '16px' }} />
                            <MuiLink style={{ fontSize: '13px' }} href={`mailto:${data.data.email}`}>
                              {data.data.email}
                            </MuiLink>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <Box display={'flex'} gap={'0.5rem'} style={{ padding: '1rem' }}>
                  <Tooltip title="Журнал событий">
                    <IconButton onClick={handleJournalOpen}>
                      <JournalIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </div>
              <Divider />
              <div style={{ padding: '1rem', display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                <Box>
                  <Typography variant="caption">Статус</Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {!!data.data.status && (
                      <Chip
                        label={giftCardStatusText[data.data.status]}
                        size="small"
                        style={{
                          backgroundColor: giftCardStatusBackground[data.data.status],
                          color: giftCardStatusColor[data.data.status]
                        }}
                      />
                    )}
                    {![GiftCardStatus.Cancelled, GiftCardStatus.Expired].includes(data.data.status) && (
                      <Tooltip title="Изменить статус">
                        <IconButton size="small" onClick={handleStatusChange}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </Box>
                <Box>
                  <Typography variant="caption">Остаток сертификата</Typography>
                  <Typography sx={{ color: data.data.balance > 0 ? '#000' : '#0000006b' }}>
                    {data.data.balance} ₽
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption">Оплата</Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Typography>{paymentStatusText[data.data.paymentStatus] || 'Не запрашивалась'}</Typography>
                    <Tooltip title="Изменить статус оплаты">
                      <IconButton size="small" onClick={handlePaymentStatusChange}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                  {data.data.paymentStatus !== GiftCardPaymentStatus.Paid && (
                    <Typography
                      variant="caption"
                      color="#5567f5"
                      style={{ cursor: 'pointer' }}
                      onClick={handlePaymentLinkClick}
                    >
                      Получить ссылку на оплату
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption">Срок действия</Typography>
                  <Typography>
                    {' '}
                    {Intl.DateTimeFormat('ru', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }).format(data.data.expiryDate)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.48 }}>
                    Номинал сертификата
                  </Typography>
                  <Typography fontSize="14px" sx={{ opacity: 0.48 }}>
                    {data.data.amount} ₽
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption">Комментарий сотрудника</Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Typography style={{ opacity: data.data.staffComment ? 1 : 0.72 }}>
                      {data.data.staffComment || 'Не указан'}
                    </Typography>
                    <Tooltip title="Изменить комментарий сотрудника">
                      <IconButton size="small" onClick={handleStaffCommentChange}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </Box>
              </div>
            </Box>
          </Card>
          {!isDeleting && ![GiftCardStatus.Cancelled, GiftCardStatus.Used].includes(data.data.status) && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: '1rem' }}>
              <Button
                type="submit"
                variant="outlined"
                color="error"
                onClick={handleDelete}
                disabled={processing || isFetching}
              >
                Удалить
              </Button>
              <Button type="submit" variant="outlined" disabled={processing || isFetching} onClick={handleRedeem}>
                Погасить
              </Button>
            </Box>
          )}
          {!!isDeleting && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3, gap: '1rem' }}>
              Вы действительно хотите удалить сертификат?
              <Button variant="outlined" onClick={cancelDelete} disabled={processing || isFetching}>
                Отмена
              </Button>
              <Button
                variant="contained"
                disableElevation
                color="error"
                disabled={processing || isFetching}
                onClick={onDelete}
              >
                Подтвердить
              </Button>
            </Box>
          )}
          <GiftStaffCommentDialog
            giftCardId={data.data.id}
            initialStaffComment={data?.data.staffComment}
            open={editStaffCommentDialogOpen}
            onClose={closeEditStaffCommentDialog}
          />
          <GiftCardStatusDialog
            giftCardId={data.data.id}
            initialStatus={data?.data.status}
            open={editStatusDialogOpen}
            onClose={closeEditStatusDialog}
          />
          <GiftCardPaymentStatusDialog
            giftCardId={data.data.id}
            initialStatus={data?.data.paymentStatus}
            open={editPaymentStatusDialogOpen}
            onClose={closeEditPaymentStatusDialog}
          />
          <GiftCardPaymentLinkDialog
            open={paymentLinkDialogOpen}
            giftCardId={data.data.id}
            onClose={closePaymentLinkDialog}
          />
          <GiftCardReedemDialog
            open={reedemDialogOpen}
            onClose={closeReedemDialog}
            giftCardId={data.data.id}
            availableBalance={data.data.balance}
          />
          <GiftCardJournalDialog open={journalIsOpened} onClose={closeJournalDialog} giftCardId={data.data.id} />
        </>
      )}
    </Container>
  )
}

export { GiftCardView }
